/**
 * Client-side image compression to reduce upload size before hitting the API.
 *
 * Strategy: Decode → downscale (canvas) → re-encode as JPEG.
 * Defaults chosen for shipment/cargo photos on mobile: 1600px max edge,
 * JPEG quality 0.82 — keeps text on kargo etiketleri readable while shrinking
 * multi-MB phone shots to ~200–400KB.
 */
export async function compressImage(
  file,
  { maxEdge = 1600, quality = 0.82, mime = "image/jpeg" } = {}
) {
  if (!file || !file.type?.startsWith("image/")) return file;

  const dataUrl = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });

  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });

  const { width, height } = img;
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  const blob = await new Promise((res) => canvas.toBlob(res, mime, quality));
  if (!blob) return file;

  const ext = mime === "image/jpeg" ? "jpg" : mime.split("/")[1] || "img";
  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${baseName}-c.${ext}`, { type: mime });
}
